import { Breakpoints, Button, Colors, Icon } from "swing-components";

import { Question } from "~onboarding/components";
import type { StepComputed, UnsyncedSubAttribute } from "~onboarding/utils";

type StepProps = {
  stepComputed: Omit<StepComputed, "prevButtonText" | "nextButtonText">;
  /**
   * When answering a Question, this function will pass the `value` to the
   * parent TaskPage.
   */
  onChange: ({ attribute, value }: UnsyncedSubAttribute) => void;
  /**
   * Disabled the ability to answer any questions.
   * @default false
   */
  isReadOnly?: boolean;
  /**
   * Helps calculate the correct "Next" button label
   * @default false
   */
  isLastStep?: boolean;
  /**
   * Button props to for next button.
   */
  nextButtonProps: {
    label?: string;
    onClick: () => void;
    /**
     * Shows a loading spinner on the button
     * @default false
     */
    isLoading?: boolean;
    /**
     * @default false
     */
    disabled?: boolean;
  };
  /**
   * Button props to for prev button.
   */
  prevButtonProps?: {
    label?: string;
    onClick: () => void;
    /**
     * @default false
     */
    disabled?: boolean;
    // NOTE: This button cannot be is a loading state.
  };
};

export function Step(props: StepProps) {
  const {
    stepComputed,
    prevButtonProps,
    nextButtonProps,
    onChange,
    isReadOnly = false,
    isLastStep = false,
  } = props;
  const { status, questions, header } = stepComputed;

  return (
    <div
      css={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        gap: "40px",
      }}
    >
      {/* Header */}
      {header && (
        <header css={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <h2
            css={{
              fontSize: 20,
              lineHeight: "30px",
              fontWeight: 600,
              margin: 0,
              color: Colors.black,
            }}
          >
            {header.title}
          </h2>

          {/* List of icon and label */}
          {header.decorations && (
            <ul
              css={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                paddingLeft: 24,
                margin: 0,
              }}
            >
              {header.decorations.map(({ icon, label }) => (
                <li key={`${header.title}-${label}`} css={{ display: "flex", gap: 16 }}>
                  <Icon name={icon} />
                  <span
                    css={{
                      fontSize: 16,
                      lineHeight: "24px",
                      fontWeight: 400,
                      color: Colors.black,
                    }}
                  >
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* Divider */}
          <hr css={{ width: "100%", background: Colors.slate300, margin: 0, border: "none" }} />
        </header>
      )}

      {/* Questions */}
      {questions.map((question) => (
        <Question
          key={question.attribute}
          {...question}
          value={question.value}
          onChange={onChange}
          isReadOnly={isReadOnly}
        />
      ))}

      {/* Buttons */}
      <div
        css={{
          display: "grid",
          gap: 8,
          // Stack the buttons on mobile
          gridTemplateRows: "1fr 1fr",
          [Breakpoints.smallDeviceAndUp]: {
            gridTemplateRows: "auto",
            gridTemplateColumns: `repeat(${!prevButtonProps ? 1 : 2}, 1fr)`,
          },
        }}
      >
        {/* Previous Button */}
        {prevButtonProps && (
          <div
            css={{
              justifySelf: "stretch",
              order: 2,
              [Breakpoints.smallDeviceAndUp]: {
                order: 1,
                justifySelf: "flex-start",
              },
            }}
          >
            <Button
              type="secondary"
              onClick={prevButtonProps.onClick}
              status={prevButtonProps.disabled ? "disabled" : "enabled"}
            >
              {/* Either the given label or "Previous" */}
              {prevButtonProps.label ?? "Previous"}
            </Button>
          </div>
        )}

        {/* Next Button */}
        {/* Only include this on mobile, or when there is no previous button */}
        <div
          css={{
            justifySelf: "stretch",
            order: 1,
            [Breakpoints.smallDeviceAndUp]: {
              order: 2,
              justifySelf: "flex-end",
            },
          }}
        >
          <Button
            data-testid="step-next-button"
            onClick={nextButtonProps.onClick}
            status={
              nextButtonProps.disabled
                ? "disabled"
                : nextButtonProps.isLoading
                  ? "loading"
                  : "enabled"
            }
            type="primary"
          >
            {getNextButtonLabel({
              label: nextButtonProps?.label,
              stepStatus: status,
              isLastStep,
              isReadOnly,
            })}
          </Button>
        </div>
      </div>
    </div>
  );
}

/********** Helper Functions *********/
/**
 * Returns the appropriate next button label following these rules:
 *
 * 1. In any case that the current Step is in a UNDER_REVIEW status, show a return home button since
 *   the user cannot move forward.
 * 2. If on the last step and NOT in a read-only state, return either the given label or "Submit"
 *   since this can be customized in the Step configuration.
 * 3. When on the last step and IN a read-only state, return "Return Home" since the user cannot move
 * forward nor control this label.
 * 4. Otherwise, default to the given label or "Next"
 * */
function getNextButtonLabel(params: {
  label?: string;
  stepStatus: StepComputed["status"];
  isLastStep: boolean;
  isReadOnly: boolean;
}) {
  const { label, stepStatus, isLastStep, isReadOnly } = params;
  // If the Step is in a UNDER_REVIEW status, show a return home button.
  if (stepStatus === "UNDER_REVIEW") return "Return Home";
  // When ON the last step and NOT IN a read-only state
  // Then return either the label or "Submit"
  if (isLastStep && !isReadOnly) return label ?? "Submit";
  // When ON the last step and IN a read-only state
  if (isLastStep && isReadOnly) return "Return Home";
  // Otherwise, default to the given label or "Next"
  return label ?? "Next";
}
